import React, { FunctionComponent, useState, Fragment } from 'react';
import classNames from 'classnames';
import { AppEnums } from '@core/enums';
import { States, Api } from '@core/types';
import { connect } from 'react-redux';
import { productActionCreators } from '@redux/products';
import { usePassportContext } from '@tti/passport';
import { PassportEnums } from '@tti/passport';
import { Modal } from '@components/shared';
import { appActionCreators } from '@redux/app';
import { projectActionCreators } from '@redux/projects';

interface ArticleNumberWithQuantity {
  articleNumber: string;
  quantity: number;
}

interface IProps {
  app?: States.IAppState;
  product?: States.IProductState;
  project?: States.IProjectState;
  importByArticleNumber: (params: Api.IFetchProductsRequest) => void;
  updateProject: (params: Api.IUpdateProjectRequest) => void;
  setActiveNav: (navigation: string) => void;
}

const ProductImport: FunctionComponent<IProps> = ({ app, product, project, importByArticleNumber, updateProject, setActiveNav }) => {
  const { passportContext, getClaim } = usePassportContext();
  const cultureClaim = getClaim(PassportEnums.ClaimType.Locality, passportContext.claims);
  const [articleNumbersWithQuantities, setArticleNumbersWithQuantities] = useState<ArticleNumberWithQuantity[]>([]);
  const [textArea, setTextArea] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const [showModal, setShowModal] = useState(false);

  if (!app || !product || !passportContext.bearerToken || !cultureClaim) {
    return null;
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.currentTarget;
    setTextArea(value);

    // Split into non-empty lines
    const lines = value.split('\n').filter((x) => x.trim().length > 0);

    if (lines.length === 0) {
      setDisableButton(true);
      setArticleNumbersWithQuantities([]);
      return;
    }

    const parsedData: ArticleNumberWithQuantity[] = [];
    let allValid = true;

    for (const line of lines) {
      const trimmedLine = line.trim();
      // Matches article number and optional quantity separated by space and/or comma
      const match = trimmedLine.match(/^([0-9]+)(?:[\s,]+([0-9]+))?$/);

      if (match) {
        const articleNumber = match[1].trim();
        const quantity = match[2] ? parseInt(match[2].trim(), 10) : 1;

        if (quantity > 0) {
          // Add to existing quantity or create new entry
          const existingEntry = parsedData.find((item) => item.articleNumber === articleNumber);
          if (existingEntry) {
            existingEntry.quantity += quantity;
          } else {
            parsedData.push({ articleNumber, quantity });
          }
        } else {
          allValid = false;
          break;
        }
      } else {
        allValid = false;
        break;
      }
    }

    if (allValid && parsedData.length > 0) {
      setDisableButton(false);
      setArticleNumbersWithQuantities(parsedData);
    } else {
      setDisableButton(true);
      setArticleNumbersWithQuantities([]);
    }
  };

  const handleSubmit = () => {
    if (
      !passportContext.bearerToken ||
      !cultureClaim ||
      !project ||
      !project.currentProject ||
      articleNumbersWithQuantities.length === 0
    ) {
      return null;
    }

    // open the modal
    setShowModal(true);

    // API call just requires article numbers to fetch products
    const articleNumbers = articleNumbersWithQuantities.map((item) => item.articleNumber);

    importByArticleNumber({
      bearerToken: passportContext.bearerToken,
      cultureCode: 'en-TT',
      articleNumbers,
    });

    // close the navigation
    setActiveNav(AppEnums.Navigation.None);
  };

  const handleAddProducts = () => {
    if (!project || !project.currentProject || !passportContext.bearerToken || !cultureClaim || product.importResults.length === 0) {
      return;
    }

    const projectGUID = project.currentProject.projectGUID;

    // Add the existing products
    const projectProducts = project.currentProject.products;

    // Add the new products with their combined quantities
    articleNumbersWithQuantities.forEach((item) => {
      const productID = product.importResults.find((x) => x.articleNumbers.includes(item.articleNumber))?.variantAgilityID;

      if (!productID) {
        return;
      }

      const existingProduct = projectProducts.find((p) => p.productID === productID);
      if (existingProduct) {
        // Update quantity
        existingProduct.quantity += item.quantity;
      } else {
        // Add new product
        projectProducts.push({
          productID: productID,
          quantity: item.quantity,
        });
      }
    });

    // Add all the products
    updateProject({
      projectGUID,
      projectProducts,
      bearerToken: passportContext.bearerToken,
      cultureCode: cultureClaim.value,
      refetchProject: true,
    });

    // hide the modal
    setShowModal(false);

    // reset the article numbers
    setArticleNumbersWithQuantities([]);

    // reset the input
    setTextArea('');

    // close the navigation
    setActiveNav(AppEnums.Navigation.None);
  };

  const productImportClassNames = classNames({
    'product-import': true,
    'is-active': app.activeNavigation === AppEnums.Navigation.Import,
  });

  const buttonClassNames = classNames({
    btn: true,
    'btn--disabled': disableButton,
  });

  const importButtonClassNames = classNames({
    btn: true,
    'btn--disabled': product.importResults.length === 0,
  });

  const results = () => {
    return product.importResults.map((x) => {
      return (
        <li key={x.variantAgilityID}>
          {x.variantName} (Quantity:{' '}
          {articleNumbersWithQuantities.find((item) => x.articleNumbers.includes(item.articleNumber))?.quantity || 1})
        </li>
      );
    });
  };

  return (
    <Fragment>
      <Modal
        onClose={() => setShowModal(false)}
        visible={showModal && product.importErrors.length === 0 && product.importResults.length > 0}
        showClose={true}
      >
        <div className="form__inner">
          <div className="form__fields">
            <h2 className="align--center">Import</h2>
            <p>Do you want to import the following products:</p>
            {results()}
          </div>
        </div>
        <button className="btn" onClick={handleAddProducts}>
          Import Products
        </button>
      </Modal>
      <Modal onClose={() => setShowModal(false)} visible={showModal && product.importErrors.length > 0} showClose={true}>
        <div className="form__inner">
          <div className="form__fields">
            <h2 className="align--center">Whoops</h2>
            <p>The following article numbers couldn't be found:</p>
            {
              <ul className="product-import__results">
                {product.importErrors.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            }
            {product.importResults.length > 0 && (
              <Fragment>
                <p>Would you like to import the rest anyway? ({product.importResults.length})</p>
                {results()}
              </Fragment>
            )}
          </div>
        </div>
        <button className={importButtonClassNames} onClick={handleAddProducts}>
          Import Products
        </button>
      </Modal>
      <div className={productImportClassNames} onClick={(e) => e.stopPropagation()}>
        <div className="product-import__container">
          <div className="product-import__title">Paste in your article numbers</div>
          <div className="product-import__import-container">
            <textarea
              value={textArea}
              onChange={handleOnChange}
              id="txtImport"
              className="product-import__import"
              placeholder="Paste article numbers one per row&#10;Format: 4933492801 or 4933492801, 2 (with quantity)"
            ></textarea>
          </div>
        </div>
        <div className="product-import__button" onClick={handleSubmit}>
          <button className={buttonClassNames}>Add Products</button>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state: States.IRootState) => ({
  app: state.app,
  product: state.product,
  project: state.project,
});

const mapDispatchToProps = {
  importByArticleNumber: (params: Api.IFetchProductsRequest) => productActionCreators.importByArticleNumber(params),
  setActiveNav: (navigation: string) => appActionCreators.setActiveNav(navigation),
  updateProject: (params: Api.IUpdateProjectRequest) => projectActionCreators.updateProject(params),
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductImport);
